
import { Parser } from 'json2csv';
import { numberToHour, timePassed, ddmmyy } from '../../../filters';
import util from '../services/utilService';
import moment from 'moment';
import * as texts from '../../../texts';
import { localeData } from './localeService';

function panelText(key) {
    const value = (texts[localeData.language] && texts[localeData.language][key]) || texts.en[key];
    if (value == null || value === '') throw new Error(`Missing panel text '${key}'`);
    return value;
}

/**@param {(string[])[]} rows*/
function rowsToCsv(rows) {
    // UTF-8 BOM so Excel detects encoding and shows Hebrew/non-ASCII correctly
    return new Blob(['\uFEFF' + rows.map(e => e.join(",")).join("\n")], { type: 'text/csv;charset=utf-8' });
}

/**@param {{
 * iconUrl: string;
 * label: string;
 * name: string;
 * text: number | string;
 * trendInfo?: { text: string; upDown: 'up' | 'down' }
 * }[]} infoBarItems*/
function getBarItemsInfoForCsv(infoBarItems) {
    /**@type {Object<string, { value: number | string; trendInfo: string; }>} */
    const barItemsInfo = infoBarItems.reduce((acc, item) => {
        acc[item.label] = {
            value: item.text,
            trendInfo: item.trendInfo ?
                `${item.trendInfo.upDown === 'down' ? '-' : ''}${item.trendInfo.text}`
                : ''
        }
        return acc;
    }, {})
    return barItemsInfo;
}


/**@typedef {import('chart.js').ChartDataSets} Datasets*/
/**
 * @param {{
 * cleaning: { labels: string[]; datasets: Datasets[] }
 * maintenance: { labels: string[]; datasets: Datasets[] }
 * general: { labels: string[]; datasets: Datasets[] }
 * }} chartListData*/
function getCsvForChartListData(chartListData) {
    if (!chartListData) {
        return _generateBlob(['General', 'general%', 'General Count'], []);
    }
    const fields = [
        'General', 'general%', 'General Count', 'Cleaning', 'cleaning%',
        'Cleaning Count', 'Maintenance', 'maintenance%', 'Maintenance Count'
    ];
    let data = [];
    for (const property in chartListData) {
        const chart = chartListData[property];
        if (!chart?.labels?.length || !chart?.datasets?.[0]?.data?.length) continue;
        const values = chart.datasets[0].data.map((val) => Number(val) || 0);
        const total = values.reduce((sum, val) => sum + val, 0) || 1;
        chart.labels.forEach((label, index) => {
            const value = values[index] ?? 0;
            const percentageNum = ((value / total) * 100).toFixed(0) + '%';
            if (property === 'general') {
                data[index] = { ...data[index], 'General': label, 'general%': percentageNum, 'General Count': value };
            } else if (property === 'cleaning') {
                data[index] = { ...data[index], 'Cleaning': label, 'cleaning%': percentageNum, 'Cleaning Count': value };
            } else if (property === 'maintenance') {
                data[index] = { ...data[index], 'Maintenance': label, 'maintenance%': percentageNum, 'Maintenance Count': value };
            }
        });
    }
    return _generateBlob(fields, data);
}

/**
 * @param {{
 * arrivals: number;
 * complaints: number;
 * likes: number;
 * maintenanceArrivals: number;
 * maintenanceComplaint: number;
 * supervisors: number;
 * time: number;
 * visits: number;
 * }[]} hourData
 */
function _formatAvgArrivals(value) {
    const amount = Number(value);
    if (!Number.isFinite(amount) || amount <= 0) return '';
    if (amount < 1) return amount.toFixed(2);
    const rounded = Math.round(amount * 10) / 10;
    return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
}

function generateHourCsv(hourData) {
    if (!hourData || !hourData.length) {
        return _generateBlob(['issue'], []);
    }
    let fields = ['issue'];
    /**@type {Array} */
    const workingDays = Number(hourData[0] && hourData[0].workingDayCount);
    const includeDaily = Number.isFinite(workingDays) && workingDays > 0;
    let data = [
        { issue: 'Visits' },
        { issue: 'C.complaints' },
        { issue: 'C.arrivals' },
        { issue: 'M.complaints' },
        { issue: 'M.arrivals' },
    ];
    if (includeDaily) {
        data.push(
            { issue: 'Visits /day' },
            { issue: 'C.complaints /day' },
            { issue: 'C.arrivals /day' },
            { issue: 'M.complaints /day' },
            { issue: 'M.arrivals /day' },
        );
    }
    hourData.forEach(hourItem => {
        const hour = numberToHour(hourItem.time);
        const days = Number(hourItem.workingDayCount);
        fields.push(hour);
        data[0] = { ...data[0], [hour]: hourItem.visits ?? 0 };
        data[1] = { ...data[1], [hour]: hourItem.complaints ?? 0 };
        data[2] = { ...data[2], [hour]: hourItem.arrivals ?? 0 };
        data[3] = { ...data[3], [hour]: hourItem.maintenanceComplaint ?? 0 };
        data[4] = { ...data[4], [hour]: hourItem.maintenanceArrivals ?? 0 };
        if (includeDaily) {
            data[5] = { ...data[5], [hour]: _formatAvgArrivals((Number(hourItem.visits) || 0) / days) };
            data[6] = { ...data[6], [hour]: _formatAvgArrivals((Number(hourItem.complaints) || 0) / days) };
            data[7] = { ...data[7], [hour]: _formatAvgArrivals((Number(hourItem.arrivals) || 0) / days) };
            data[8] = { ...data[8], [hour]: _formatAvgArrivals((Number(hourItem.maintenanceComplaint) || 0) / days) };
            data[9] = { ...data[9], [hour]: _formatAvgArrivals((Number(hourItem.maintenanceArrivals) || 0) / days) };
        }
    });
    return _generateBlob(fields, data);
}

/**
 * @param {{
 * _id: number | string;
 * arrivals:number;
 * avgResponseTime: number;
 * complaints: number;
 * displayName: string;
 * missedSla: number;
 * score: number;
 * trend: 'up' | 'down';
 * visits: number;
 * }[]} roomsData
 */
function generateRoomCsv(roomsData) {
    const noCallArrivalsLabel = panelText('unnecessary_arrivals');
    const fields = [
        'Room Name', 'Visits', 'Complaints', 'Cleaner Arrivals', noCallArrivalsLabel, 'Supervisors',
        'Counter Calls', 'Cleandex calls', 'Manual calls',
        'Avg. Response Time', 'Room Activity', 'Score',
    ];
    const data = (roomsData || []).map(room => ({
        'Room Name': room.displayName,
        'Visits': room.visits,
        'Complaints': room.complaints,
        'Cleaner Arrivals': room.arrivals,
        [noCallArrivalsLabel]: room.unnecessaryArrivals != null ? room.unnecessaryArrivals : '',
        'Supervisors': room.supervisors,
        'Counter Calls': room.counterCalls,
        'Cleandex calls': room.cleandexCalls,
        'Manual calls': room.manualCalls,
        'Avg. Response Time': timePassed(room.avgResponseTime),
        'Room Activity': room.exposures ? room.exposures.toFixed()+ '%' : '',
        'Score': room.score != null ? room.score.toFixed(0) : '',
    }));
    return _generateBlob(fields, data);
}

/**
 * @param {{ name: string; value: string | number; trend: string }[]} rows
 */
function generateMainBarCsv(rows) {
    const fields = ['name', 'value', 'trend'];
    const data = rows.map((row) => ({
        name: row.name,
        value: row.value == null ? '' : row.value,
        trend: row.trend == null ? '' : row.trend,
    }));
    return _generateBlob(fields, data);
}

/**
 * Builds the per-room arrivals-by-hour CSV. One row per room: Room Name, Total Arrived, then a column
 * per hour (only hours with data across rooms).
 *
 * Hour cells come from the stored hour_map so they match the panel hour table 1:1, while "Total Arrived"
 * comes from the shift-wide count so the sheet total matches the panel KPI and room.csv. For cleaners the
 * two disagree on purpose: the hour_map dedups arrivals within each hour, so a pair straddling an hour
 * boundary shows up in two hour cells but counts once in the total.
 * @param {Object<string, Object<number, number>>} arrivalByRoom - roomId -> { hourIndex: count }
 * @param {Object<string, number>} totalByRoom - roomId -> shift-wide total
 * @param {Object<string, { name: string }>} roomIdMap - roomId -> { name: string }
 * @returns {Blob}
 */
function generateArrivalByHourCsv(arrivalByRoom, totalByRoom, roomIdMap) {
    const hoursSet = new Set();
    for (const roomId in arrivalByRoom) {
        for (const hour in arrivalByRoom[roomId]) hoursSet.add(Number(hour));
    }
    const hours = [...hoursSet].sort((a, b) => a - b);
    const hourLabels = hours.map((h) => numberToHour(h));
    const fields = ['Room Name', 'Total Arrived', ...hourLabels];

    const data = [];
    let grandTotal = 0;
    for (const roomId in arrivalByRoom) {
        const roomName = roomIdMap[roomId] ? roomIdMap[roomId].name : roomId;
        const row = { 'Room Name': roomName };
        hours.forEach((hour, index) => {
            row[hourLabels[index]] = arrivalByRoom[roomId][hour] || 0;
        });
        const total = totalByRoom[roomId];
        if (!Number.isFinite(total)) {
            throw new Error(`Missing shift-wide arrivals total for room ${roomId} in arrivals export`);
        }
        row['Total Arrived'] = total;
        grandTotal += total;
        data.push(row);
    }
    if (data.length) {
        const totalRow = { 'Room Name': 'TOTAL', 'Total Arrived': grandTotal };
        hours.forEach((hour, index) => {
            totalRow[hourLabels[index]] = data.reduce((sum, r) => sum + (r[hourLabels[index]] || 0), 0);
        });
        data.push(totalRow);
    }
    return _generateBlob(fields, data);
}

/**
 * @param {Object<string, string[][]>} cleanerArrivalData
 * @returns {Object<string, Blob>}
 */
function generateArrivalCsv(arrivalData) {
    /**@type {Object<string, Blob>} */
    let roomIdBlobMap = {};
    for (const roomId in arrivalData) {
        const length = util.lengthOfLongestArrayInMatrix(arrivalData[roomId]);
        const fields = ['Date', 'Total Arrived'];
        for (let i = 1; i <= length; i++) {
            fields.push(`${i}`);
        }
        let data = [];
        arrivalData[roomId].forEach(dayArrivals => {
            dayArrivals = dayArrivals.sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
            const day = ddmmyy(dayArrivals[0]);
            const totalArrivals = dayArrivals.length;
            let dayData = { 'Date': day, 'Total Arrived': totalArrivals };
            dayArrivals.forEach((timeString, index) => dayData[index + 1] = moment(new Date(timeString)).format('HH:mm'));
            data.push(dayData);
        })
        roomIdBlobMap[roomId] = _generateBlob(fields, data);
    }
    return roomIdBlobMap;
}

/**
 * @param {string[]} fields
 * @param {Object[]} data
 */
function _generateBlob(fields, data) {
    const json2csvParser = new Parser({ fields });
    // UTF-8 BOM so Excel detects encoding and shows Hebrew/non-ASCII correctly
    return new Blob(['\uFEFF' + json2csvParser.parse(data)], { type: 'text/csv;charset=utf-8;' });
}

/**
 * @param {{ Field: string; Value: string }[]} rows
 */
function generateFilterSummaryCsv(rows) {
    const fields = ['Field', 'Value'];
    const data = rows.map((row) => ({
        Field: row.Field,
        Value: row.Value == null ? '' : String(row.Value),
    }));
    return _generateBlob(fields, data);
}

export default {
    rowsToCsv,
    getBarItemsInfoForCsv,
    getCsvForChartListData,
    generateHourCsv,
    generateRoomCsv,
    generateMainBarCsv,
    generateArrivalCsv,
    generateArrivalByHourCsv,
    generateFilterSummaryCsv,
}